import { useMemo, useState } from "react";
import type { Moment } from "moment";
import { taskService } from "@miplatform/api";
import { PrioritySchedulerColumnEvent } from "@miplatform/components";
import {
  AUTO_ASSIGN_STAGING_REASONS,
  DATE_TIME_FORMATS,
  HTTP_STATUSES,
  MIN_WORK_ORDER_EXPECTED_DURATION_MINUTES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IAttemptListItemResponseForWorkOrder,
  ICalendarSet,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { formatDate, getDate, isSame } from "@miplatform/utils";
import { useAutoAssignWorkOrders } from "./useAutoAssignWorkOrders";

export const NHC_ASSIGNMENT_BOARD_DAY_COUNT = 5;

export const ASSIGNMENT_MODES = {
  UNASSIGNED: "unassigned",
  NOT_READY: "notReady",
} as const;

export type AssignmentMode =
  (typeof ASSIGNMENT_MODES)[keyof typeof ASSIGNMENT_MODES];

const emptyStagingState: StagingState = {
  stagedByUserId: {},
  stagingStack: [],
};

const EMPTY_STAGED: StagedWorkOrderAssignment[] = [];
const DEFAULT_DAY_START_TIME = "08:00";
let transferIdCounter = 0;
const EMPTY_TEMPLATE_REMAINING: Record<string, number> = {};
const EMPTY_TEMPLATE_WINDOWS: Record<string, TimeWindowRange[]> = {};

export function useAssignmentStaging(
  calendarSet?: ICalendarSet | null,
  assignmentDate?: Moment | null,
  calendarEvents: IAttemptListItemResponseForWorkOrder[] = []
) {
  const [stagingState, setStagingState] =
    useState<StagingState>(emptyStagingState);
  const [confirmedAttemptIds, setConfirmedAttemptIds] = useState<Set<string>>(
    () => new Set<string>()
  );
  const stateManager = useComponentState();
  const { autoAssignWorkOrders, isAutoAssignLoading } = useAutoAssignWorkOrders(
    calendarSet?.id
  );

  const { stagedByUserId } = stagingState;
  const isConfirming = stateManager.isBusy();
  const templateWindowMinutesById = useMemo(
    () => buildTemplateWindowMinutesById(calendarSet?.workTemplateBases ?? []),
    [calendarSet?.workTemplateBases]
  );
  const templateWindowsById = useMemo(
    () => buildTemplateWindowsById(calendarSet?.workTemplateBases ?? []),
    [calendarSet?.workTemplateBases]
  );

  const stagingMeta = useMemo(() => {
    const ids = new Set<string>();
    let total = 0;
    for (const entries of Object.values(stagedByUserId)) {
      total += entries.length;
      for (const s of entries) {
        ids.add(s.attempt.id);
      }
    }
    return { ids, total };
  }, [stagedByUserId]);

  const stagedIds = stagingMeta.ids;
  const totalStagedCount = stagingMeta.total;

  const stageWorkOrderForUser = (
    attempt: IAttemptListItemResponseForWorkOrder,
    userId: string
  ): StageAttemptResult => {
    if (hasStagedAttemptId(stagedByUserId, attempt.id)) {
      return {
        staged: false,
        reason:
          AUTO_ASSIGN_STAGING_REASONS.ALREADY_STAGED as StageAttemptBlockReason,
      };
    }
    if (
      !canStageInTemplateWindow({
        attempt,
        userId,
        assignmentDate,
        calendarEvents,
        existingStagedForUser: stagedByUserId[userId] ?? [],
        templateWindowMinutesById,
        templateWindowsById,
      })
    ) {
      return {
        staged: false,
        reason:
          AUTO_ASSIGN_STAGING_REASONS.INSUFFICIENT_WINDOW_CAPACITY as StageAttemptBlockReason,
      };
    }
    applyAssigneeExpectedDuration(
      attempt,
      userId,
      calendarSet?.workTemplateBases ?? []
    );
    const transferId = createTransferId();
    setStagingState((prev) => {
      const existing = prev.stagedByUserId[userId] ?? [];
      const shouldUseTemplateWindow =
        hasTemplateWindowForAttempt(attempt, templateWindowMinutesById) &&
        !!assignmentDate;
      const staged = createStagedAssignment({
        attempt,
        userId,
        calendarSet,
        assignmentDate,
        calendarEvents,
        existingStagedForUser: existing,
        forceSelectedDay: shouldUseTemplateWindow,
        templateWindowsById,
        templateWindowMinutesById,
      });

      return {
        stagedByUserId: {
          ...prev.stagedByUserId,
          [userId]: [...existing, staged],
        },
        stagingStack: [
          ...prev.stagingStack,
          { userId, attemptId: attempt.id, transferId },
        ],
      };
    });
    return { staged: true };
  };

  const stageWorkOrdersForUser = (
    attempts: IAttemptListItemResponseForWorkOrder[],
    userId: string
  ): StageAttemptsResult => {
    if (!attempts.length) {
      return { addedCount: 0, blockedCount: 0 };
    }
    const transferId = createTransferId();
    let addedCount = 0;
    let blockedCount = 0;
    setStagingState((prev) => {
      const existing = prev.stagedByUserId[userId] ?? [];
      const existingAttemptIds = new Set<string>();
      for (const stagedByUserEntries of Object.values(prev.stagedByUserId)) {
        for (const staged of stagedByUserEntries) {
          existingAttemptIds.add(staged.attempt.id);
        }
      }

      let nextExisting = existing;
      const addedStackEntries: StagingStackEntry[] = [];

      for (const attempt of attempts) {
        if (!existingAttemptIds.has(attempt.id)) {
          const canStage = canStageInTemplateWindow({
            attempt,
            userId,
            assignmentDate,
            calendarEvents,
            existingStagedForUser: nextExisting,
            templateWindowMinutesById,
            templateWindowsById,
          });
          if (!canStage) {
            blockedCount += 1;
          } else {
            applyAssigneeExpectedDuration(
              attempt,
              userId,
              calendarSet?.workTemplateBases ?? []
            );
            const shouldUseTemplateWindow =
              hasTemplateWindowForAttempt(attempt, templateWindowMinutesById) &&
              !!assignmentDate;
            const staged = createStagedAssignment({
              attempt,
              userId,
              calendarSet,
              assignmentDate,
              calendarEvents,
              existingStagedForUser: nextExisting,
              forceSelectedDay: shouldUseTemplateWindow,
              templateWindowsById,
              templateWindowMinutesById,
            });

            nextExisting = [...nextExisting, staged];
            existingAttemptIds.add(attempt.id);
            addedStackEntries.push({
              userId,
              attemptId: attempt.id,
              transferId,
            });
            addedCount += 1;
          }
        }
      }

      if (!addedStackEntries.length) {
        return prev;
      }

      return {
        stagedByUserId: {
          ...prev.stagedByUserId,
          [userId]: nextExisting,
        },
        stagingStack: [...prev.stagingStack, ...addedStackEntries],
      };
    });
    return { addedCount, blockedCount };
  };

  const undoLastTransfer = () => {
    setStagingState((prev) => {
      if (!prev.stagingStack.length) {
        return prev;
      }
      const last = prev.stagingStack.at(-1);
      if (!last) {
        return prev;
      }
      const attemptIdsToRemove = new Set<string>();
      const nextStack = prev.stagingStack.filter((entry) => {
        if (entry.transferId === last.transferId) {
          attemptIdsToRemove.add(entry.attemptId);
          return false;
        }
        return true;
      });
      if (!attemptIdsToRemove.size) {
        return prev;
      }
      return {
        stagingStack: nextStack,
        stagedByUserId: removeAttemptsFromUserMap(
          prev.stagedByUserId,
          last.userId,
          attemptIdsToRemove
        ),
      };
    });
  };

  const clearStaging = () => {
    for (const entries of Object.values(stagedByUserId)) {
      restoreAssigneeExpectedDurations(entries);
    }
    setStagingState(emptyStagingState);
  };

  const confirmStaging = async () => {
    if (stateManager.isBusy()) {
      return false;
    }

    const data = buildBulkAssignPayload({
      stagedByUserId,
      assignmentDate,
    });

    if (!data.length) {
      return false;
    }

    try {
      const response = await stateManager.run(() =>
        taskService.attempts.bulkAssign({
          data,
          toastMessageConfig: undefined,
        })
      );
      if (response.status === HTTP_STATUSES.OK) {
        setConfirmedAttemptIds((prev) => {
          const next = new Set(prev);
          for (const payloadItem of data) {
            next.add(payloadItem.AttemptId);
          }
          return next;
        });
        clearStaging();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const clearStagingForUser = (userId: string) => {
    setStagingState((prev) => {
      const hasStagedForUser = Boolean(prev.stagedByUserId[userId]?.length);
      const hasStackForUser = prev.stagingStack.some(
        (entry) => entry.userId === userId
      );
      if (!hasStagedForUser && !hasStackForUser) {
        return prev;
      }
      const nextMap = hasStagedForUser
        ? { ...prev.stagedByUserId }
        : prev.stagedByUserId;
      if (hasStagedForUser) {
        restoreAssigneeExpectedDurations(prev.stagedByUserId[userId]);
        delete nextMap[userId];
      }
      const nextStack = hasStackForUser
        ? prev.stagingStack.filter((entry) => entry.userId !== userId)
        : prev.stagingStack;
      return {
        stagedByUserId: nextMap,
        stagingStack: nextStack,
      };
    });
  };

  const unstageAttempt = (userId: string, attemptId: string) => {
    setStagingState((prev) => {
      const list = prev.stagedByUserId[userId];
      if (!list?.some((s) => s.attempt.id === attemptId)) {
        return prev;
      }
      const nextStack = prev.stagingStack.filter(
        (entry) => !(entry.userId === userId && entry.attemptId === attemptId)
      );
      return {
        stagingStack:
          nextStack.length === prev.stagingStack.length
            ? prev.stagingStack
            : nextStack,
        stagedByUserId: removeAttemptFromUserMap(
          prev.stagedByUserId,
          userId,
          attemptId
        ),
      };
    });
  };

  const getStagedForUser = (userId: string) =>
    stagedByUserId[userId] ?? EMPTY_STAGED;

  const getRemainingTemplateDurationForUser = (
    userId: string
  ): Record<string, number> => {
    if (!assignmentDate) return EMPTY_TEMPLATE_REMAINING;
    if (!Object.keys(templateWindowMinutesById).length)
      return EMPTY_TEMPLATE_REMAINING;

    return getTemplateRemainingMinutesForUserOnDay({
      day: assignmentDate.clone().startOf("day"),
      userId,
      calendarEvents,
      stagedForUser: getStagedForUser(userId),
      templateWindowMinutesById,
      templateWindowsById,
    });
  };

  return {
    stagedByUserId,
    stagedIds,
    confirmedAttemptIds,
    totalStagedCount,
    isConfirming,
    stageWorkOrderForUser,
    stageWorkOrdersForUser,
    undoLastTransfer,
    confirmStaging,
    clearStaging,
    clearStagingForUser,
    unstageAttempt,
    getStagedForUser,
    getRemainingTemplateDurationForUser,
    autoAssignWorkOrders,
    isAutoAssignLoading,
  };
}

export type StagedWorkOrderAssignment = {
  attempt: IAttemptListItemResponseForWorkOrder;
  dayOffset: number;
  scheduledStartDate: string;
};

export function toBoardEventFromStagedAssignment(
  staged: StagedWorkOrderAssignment
): PrioritySchedulerColumnEvent {
  return {
    ...staged.attempt,
    isStaged: true,
    scheduledStartDate: staged.scheduledStartDate,
  };
}

type StagingStackEntry = {
  userId: string;
  attemptId: string;
  transferId: string;
};

type StagingState = {
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>;
  stagingStack: StagingStackEntry[];
};

type StageAttemptBlockReason =
  | "already-staged"
  | "insufficient-window-capacity";

export type StageAttemptResult = {
  staged: boolean;
  reason?: StageAttemptBlockReason;
};

export type StageAttemptsResult = {
  addedCount: number;
  blockedCount: number;
};

function hasStagedAttemptId(
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>,
  attemptId: string
): boolean {
  for (const userId of Object.keys(stagedByUserId)) {
    const entries = stagedByUserId[userId];
    if (entries?.some((s) => s.attempt.id === attemptId)) {
      return true;
    }
  }
  return false;
}

function restoreAssigneeExpectedDuration(
  attempt: IAttemptListItemResponseForWorkOrder
) {
  attempt.assigneeExpectedDuration = attempt.expectedDuration;
}

function restoreAssigneeExpectedDurations(
  entries: StagedWorkOrderAssignment[] | undefined
) {
  if (!entries?.length) {
    return;
  }
  for (const staged of entries) {
    restoreAssigneeExpectedDuration(staged.attempt);
  }
}

function applyAssigneeExpectedDuration(
  attempt: IAttemptListItemResponseForWorkOrder,
  userId: string,
  workTemplateBases: ICalendarWorkTemplateBase[]
) {
  attempt.assigneeExpectedDuration =
    getExpectedDurationMinutesWithPerformanceMultiplier(
      attempt,
      userId,
      workTemplateBases
    );
}

function removeAttemptFromUserMap(
  prev: Record<string, StagedWorkOrderAssignment[]>,
  userId: string,
  attemptId: string
): Record<string, StagedWorkOrderAssignment[]> {
  const list = prev[userId];
  if (!list?.length) {
    return prev;
  }
  const removed = list.find((s) => s.attempt.id === attemptId);
  if (!removed) {
    return prev;
  }
  restoreAssigneeExpectedDuration(removed.attempt);
  const nextList = list.filter((s) => s.attempt.id !== attemptId);
  const next = { ...prev };
  if (nextList.length === 0) {
    delete next[userId];
  } else {
    next[userId] = nextList;
  }
  return next;
}

function removeAttemptsFromUserMap(
  prev: Record<string, StagedWorkOrderAssignment[]>,
  userId: string,
  attemptIds: ReadonlySet<string>
): Record<string, StagedWorkOrderAssignment[]> {
  const list = prev[userId];
  if (!list?.length || !attemptIds.size) {
    return prev;
  }
  const nextList = list.filter((s) => !attemptIds.has(s.attempt.id));
  if (nextList.length === list.length) {
    return prev;
  }
  for (const staged of list) {
    if (attemptIds.has(staged.attempt.id)) {
      restoreAssigneeExpectedDuration(staged.attempt);
    }
  }
  const next = { ...prev };
  if (nextList.length === 0) {
    delete next[userId];
  } else {
    next[userId] = nextList;
  }

  return next;
}

function createTransferId(): string {
  transferIdCounter += 1;
  return `transfer-${transferIdCounter}`;
}

function createStagedAssignment({
  attempt,
  userId,
  calendarSet,
  assignmentDate,
  calendarEvents,
  existingStagedForUser,
  forceSelectedDay = false,
  templateWindowsById = EMPTY_TEMPLATE_WINDOWS,
  templateWindowMinutesById = EMPTY_TEMPLATE_REMAINING,
}: {
  attempt: IAttemptListItemResponseForWorkOrder;
  userId: string;
  calendarSet?: ICalendarSet | null;
  assignmentDate?: Moment | null;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
  existingStagedForUser: StagedWorkOrderAssignment[];
  forceSelectedDay?: boolean;
  templateWindowsById?: Record<string, TimeWindowRange[]>;
  templateWindowMinutesById?: Record<string, number>;
}): StagedWorkOrderAssignment {
  const dayOffset = forceSelectedDay
    ? 0
    : findDayOffsetForStagedAttempt(
        calendarSet,
        assignmentDate,
        userId,
        calendarEvents,
        existingStagedForUser,
        attempt,
        NHC_ASSIGNMENT_BOARD_DAY_COUNT,
        templateWindowMinutesById,
        templateWindowsById
      );
  const day =
    assignmentDate?.clone().startOf("day").add(dayOffset, "days") ??
    getDate().startOf("day");
  const stagedOnDay = existingStagedForUser.filter(
    (s) => s.dayOffset === dayOffset
  );
  const cursor = getScheduleCursorForAttemptOnDay({
    day,
    attempt,
    userId,
    calendarEvents,
    stagedOnDay,
    calendarSet,
    templateWindowsById,
  });

  return {
    attempt,
    dayOffset,
    scheduledStartDate: cursor.toISOString(),
  };
}

type TimeWindowRange = {
  startMinutes: number;
  endMinutes: number;
};

function getNowMinutesFromMidnight(): number {
  const now = getDate();
  return now.hours() * 60 + now.minutes();
}

function isToday(day: Moment): boolean {
  return isSame({
    date1: day.toDate(),
    date2: getDate().toDate(),
    timeUnit: "day",
  });
}

function getEffectiveWindows(
  windows: TimeWindowRange[],
  day: Moment | null | undefined
): TimeWindowRange[] {
  if (!day || !isToday(day)) {
    return windows;
  }
  const nowMinutes = getNowMinutesFromMidnight();
  return windows
    .map((w) => ({
      startMinutes: Math.max(w.startMinutes, nowMinutes),
      endMinutes: w.endMinutes,
    }))
    .filter((w) => w.endMinutes > w.startMinutes);
}

function getEffectiveTemplateWindowMinutes(
  windows: TimeWindowRange[],
  day: Moment | null | undefined
): number {
  const effective = getEffectiveWindows(windows, day);
  return effective.reduce((sum, w) => sum + (w.endMinutes - w.startMinutes), 0);
}

export function getScheduleCursorForAttemptOnDay({
  day,
  attempt,
  userId,
  calendarEvents,
  stagedOnDay,
  calendarSet,
  templateWindowsById,
}: {
  day: Moment;
  attempt: IAttemptListItemResponseForWorkOrder;
  userId: string;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
  stagedOnDay: StagedWorkOrderAssignment[];
  calendarSet?: ICalendarSet | null;
  templateWindowsById: Record<string, TimeWindowRange[]>;
}): Moment {
  const templateId = attempt.workOrderTemplateId;
  const templateWindows = templateId
    ? templateWindowsById[templateId]
    : undefined;
  if (!templateId || !templateWindows?.length) {
    return getScheduleCursorAfterEventsOnDay({
      day,
      userId,
      calendarEvents,
      stagedOnDay,
      calendarSet,
    });
  }

  const effectiveWindows = getEffectiveWindows(templateWindows, day);
  if (!effectiveWindows.length) {
    return getScheduleCursorAfterEventsOnDay({
      day,
      userId,
      calendarEvents,
      stagedOnDay,
      calendarSet,
    });
  }

  const usedTemplateMinutes =
    getUsedTemplateMinutesForDay(
      calendarEventsForUserOnDay(calendarEvents, userId, day),
      templateId
    ) +
    getUsedTemplateMinutesForDay(
      stagedOnDay.map((staged) => staged.attempt),
      templateId
    );
  return getMomentFromTemplateWindowsOffset(
    day,
    effectiveWindows,
    usedTemplateMinutes
  );
}

function getUsedTemplateMinutesForDay(
  attempts: IAttemptListItemResponseForWorkOrder[],
  templateId: string
): number {
  return attempts.reduce((sum, entry) => {
    if (entry.workOrderTemplateId !== templateId) {
      return sum;
    }
    return sum + getExpectedDurationMinutes(entry);
  }, 0);
}

function getMomentFromTemplateWindowsOffset(
  day: Moment,
  windows: TimeWindowRange[],
  offsetMinutes: number
): Moment {
  const dayStart = day.clone().startOf("day");
  let remainingOffset = Math.max(0, offsetMinutes);

  for (const window of windows) {
    const span = Math.max(0, window.endMinutes - window.startMinutes);
    if (span > 0) {
      if (remainingOffset < span) {
        return dayStart
          .clone()
          .add(window.startMinutes + remainingOffset, "minutes");
      }
      remainingOffset -= span;
    }
  }

  const fallback = windows.at(-1);
  return dayStart.clone().add(fallback?.endMinutes ?? 0, "minutes");
}

function hasTemplateWindowForAttempt(
  attempt: IAttemptListItemResponseForWorkOrder,
  templateWindowMinutesById: Record<string, number>
): boolean {
  const templateId = attempt.workOrderTemplateId;
  if (!templateId) {
    return false;
  }
  return templateWindowMinutesById[templateId] != null;
}

export function canStageInTemplateWindow({
  attempt,
  userId,
  assignmentDate,
  calendarEvents,
  existingStagedForUser,
  templateWindowMinutesById,
  templateWindowsById = EMPTY_TEMPLATE_WINDOWS,
}: {
  attempt: IAttemptListItemResponseForWorkOrder;
  userId: string;
  assignmentDate?: Moment | null;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
  existingStagedForUser: StagedWorkOrderAssignment[];
  templateWindowMinutesById: Record<string, number>;
  templateWindowsById?: Record<string, TimeWindowRange[]>;
}): boolean {
  if (!assignmentDate) {
    return true;
  }
  const templateId = attempt.workOrderTemplateId;
  if (!templateId || templateWindowMinutesById[templateId] == null) {
    return true;
  }
  const day = assignmentDate.clone().startOf("day");
  const remainingByTemplate = getTemplateRemainingMinutesForUserOnDay({
    day,
    userId,
    calendarEvents,
    stagedForUser: existingStagedForUser,
    templateWindowMinutesById,
    templateWindowsById,
  });
  return remainingByTemplate[templateId] > 0;
}

export function getTemplateRemainingMinutesForUserOnDay({
  day,
  userId,
  calendarEvents,
  stagedForUser,
  templateWindowMinutesById,
  templateWindowsById = EMPTY_TEMPLATE_WINDOWS,
}: {
  day: Moment;
  userId: string;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
  stagedForUser: StagedWorkOrderAssignment[];
  templateWindowMinutesById: Record<string, number>;
  templateWindowsById?: Record<string, TimeWindowRange[]>;
}): Record<string, number> {
  const remaining: Record<string, number> = {};
  for (const templateId of Object.keys(templateWindowMinutesById)) {
    const windows = templateWindowsById[templateId];
    remaining[templateId] = windows
      ? getEffectiveTemplateWindowMinutes(windows, day)
      : templateWindowMinutesById[templateId];
  }
  const consume = (attempt: IAttemptListItemResponseForWorkOrder) => {
    const templateId = attempt.workOrderTemplateId;
    if (!templateId || remaining[templateId] == null) {
      return;
    }
    remaining[templateId] = Math.max(
      0,
      remaining[templateId] - getExpectedDurationMinutes(attempt)
    );
  };

  for (const event of calendarEventsForUserOnDay(calendarEvents, userId, day)) {
    consume(event);
  }

  for (const staged of stagedForUser) {
    if (
      isSame({
        date1: staged.scheduledStartDate,
        date2: day.toDate(),
        timeUnit: "day",
      })
    ) {
      consume(staged.attempt);
    }
  }

  return remaining;
}

export function parseWindowTimeToMinutes(time: string): number {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

export function buildTemplateWindowMinutesById(
  workTemplateBases: Array<{
    id: string;
    autoAssignWindows: Array<{ start: string; end: string }>;
  }>
): Record<string, number> {
  return workTemplateBases.reduce<Record<string, number>>((acc, template) => {
    if (!template.autoAssignWindows.length) {
      return acc;
    }
    const minutes = template.autoAssignWindows.reduce((sum, window) => {
      const windowMinutes =
        parseWindowTimeToMinutes(window.end) -
        parseWindowTimeToMinutes(window.start);
      return sum + Math.max(0, windowMinutes);
    }, 0);
    acc[template.id] = Math.max(0, minutes);
    return acc;
  }, {});
}

function buildTemplateWindowsById(
  workTemplateBases: Array<{
    id: string;
    autoAssignWindows: Array<{ start: string; end: string }>;
  }>
): Record<string, TimeWindowRange[]> {
  return workTemplateBases.reduce<Record<string, TimeWindowRange[]>>(
    (acc, template) => {
      if (!template.autoAssignWindows.length) {
        return acc;
      }
      const windows = template.autoAssignWindows
        .map((window) => ({
          startMinutes: parseWindowTimeToMinutes(window.start),
          endMinutes: parseWindowTimeToMinutes(window.end),
        }))
        .filter((window) => window.endMinutes > window.startMinutes)
        .sort((a, b) => a.startMinutes - b.startMinutes);
      if (!windows.length) {
        return acc;
      }
      acc[template.id] = windows;
      return acc;
    },
    {}
  );
}

export function getExpectedDurationMinutes(
  attempt: IAttemptListItemResponseForWorkOrder
): number {
  return (
    attempt.assigneeExpectedDuration ??
    attempt.expectedDuration ??
    MIN_WORK_ORDER_EXPECTED_DURATION_MINUTES
  );
}

export function getExpectedDurationMinutesWithPerformanceMultiplier(
  attempt: IAttemptListItemResponseForWorkOrder,
  userId?: string,
  workTemplateBases?: ICalendarWorkTemplateBase[]
): number {
  const workTemplateBase = workTemplateBases?.find(
    (template) => template.id === attempt.workOrderTemplateId
  );
  const performanceMultiplier =
    workTemplateBase?.resourceAssignments?.find(
      (resource) => resource.resourceId === userId
    )?.performanceMultiplierPercentage ?? 0;

  const expectedDurationMinutes =
    attempt.expectedDuration ?? MIN_WORK_ORDER_EXPECTED_DURATION_MINUTES;

  return (
    expectedDurationMinutes -
    expectedDurationMinutes * performanceMultiplier * 0.01
  );
}

function getDayStartMoment(day: Moment, dayStartTime: string): Moment {
  const dayKey = formatDate(day, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
  const start = getDate(`${dayKey} ${dayStartTime}`);
  if (!start.isValid()) {
    return day.clone().startOf("day");
  }
  return start;
}

function getDayEndMoment(
  day: Moment,
  calendarSet: ICalendarSet | null | undefined
): Moment | null {
  const dayEndStr = calendarSet?.dayEndTime;
  if (!dayEndStr) {
    return null;
  }
  const dayKey = formatDate(day, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
  const end = getDate(`${dayKey} ${dayEndStr}`);
  if (!end.isValid()) {
    return null;
  }
  return end;
}
export function getAttemptEndOnDay(
  attempt: IAttemptListItemResponseForWorkOrder,
  day: Moment,
  dayStartTime: string,
  stagedScheduledStartDate?: string
): Moment {
  if (stagedScheduledStartDate) {
    return getDate(stagedScheduledStartDate).add(
      getExpectedDurationMinutes(attempt),
      "minutes"
    );
  }
  if (attempt.scheduledEndDate) {
    return getDate(attempt.scheduledEndDate);
  }
  if (attempt.startDate && attempt.endDate) {
    return getDate(attempt.endDate);
  }
  const startSource = attempt.scheduledStartDate ?? attempt.startDate;
  if (startSource) {
    return getDate(startSource).add(
      getExpectedDurationMinutes(attempt),
      "minutes"
    );
  }
  return getDayStartMoment(day, dayStartTime).add(
    getExpectedDurationMinutes(attempt),
    "minutes"
  );
}

export function getScheduleCursorAfterEventsOnDay({
  day,
  userId,
  calendarEvents,
  stagedOnDay,
  calendarSet,
}: {
  day: Moment;
  userId: string;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
  stagedOnDay: StagedWorkOrderAssignment[];
  calendarSet?: ICalendarSet | null;
}): Moment {
  const dayStartTime = calendarSet?.dayStartTime ?? DEFAULT_DAY_START_TIME;
  let cursor = getDayStartMoment(day, dayStartTime);

  for (const event of calendarEventsForUserOnDay(calendarEvents, userId, day)) {
    const end = getAttemptEndOnDay(event, day, dayStartTime);
    if (end.isAfter(cursor)) {
      cursor = end;
    }
  }

  for (const staged of stagedOnDay) {
    const end = getAttemptEndOnDay(
      staged.attempt,
      day,
      dayStartTime,
      staged.scheduledStartDate
    );
    if (end.isAfter(cursor)) {
      cursor = end;
    }
  }

  return cursor;
}

export function fitsOnDayAfterEvents({
  day,
  cursor,
  newMinutes,
  calendarSet,
}: {
  day: Moment;
  cursor: Moment;
  newMinutes: number;
  calendarSet?: ICalendarSet | null;
}): boolean {
  const dayEnd = getDayEndMoment(day, calendarSet);
  if (!dayEnd) {
    return true;
  }
  return cursor.clone().add(newMinutes, "minutes").isSameOrBefore(dayEnd);
}

function getCalendarDayWorkingMinutes(
  calendarSet: ICalendarSet | null | undefined,
  day: Moment
): number | null {
  const dayStartStr = calendarSet?.dayStartTime;
  const dayEndStr = calendarSet?.dayEndTime;
  if (!dayStartStr || !dayEndStr) {
    return null;
  }
  const dayKey = formatDate(day, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
  const start = getDate(`${dayKey} ${dayStartStr}`);
  const end = getDate(`${dayKey} ${dayEndStr}`);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return Math.max(0, end.diff(start, "minutes"));
}

export function calendarEventsForUserOnDay(
  events: IAttemptListItemResponseForWorkOrder[],
  userId: string,
  day: Moment
): IAttemptListItemResponseForWorkOrder[] {
  return events.filter((e) => {
    if (e.assignedUserId !== userId) {
      return false;
    }
    const sd = e.scheduledStartDate ?? e.startDate ?? e.scheduleDate;
    if (!sd) {
      return false;
    }
    return isSame({ date1: sd, date2: day.toDate(), timeUnit: "day" });
  });
}

type BulkAssignAttemptPayload = {
  AttemptId: string;
  UserId: string;
  ScheduledStartDate: string;
  ScheduledEndDate: string;
};

function buildBulkAssignPayload({
  stagedByUserId,
  assignmentDate,
}: {
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>;
  assignmentDate?: Moment | null;
}): BulkAssignAttemptPayload[] {
  if (!assignmentDate) {
    return [];
  }

  const payload: BulkAssignAttemptPayload[] = [];

  for (const [userId, entries] of Object.entries(stagedByUserId)) {
    if (entries.length) {
      const sortedEntries = [...entries].sort((a, b) => {
        if (a.dayOffset !== b.dayOffset) {
          return a.dayOffset - b.dayOffset;
        }
        return (
          getDate(a.scheduledStartDate).valueOf() -
          getDate(b.scheduledStartDate).valueOf()
        );
      });

      for (const { attempt, scheduledStartDate } of sortedEntries) {
        const start = getDate(scheduledStartDate);
        const duration = getExpectedDurationMinutes(attempt);
        const end = start.clone().add(duration, "minutes");

        payload.push({
          AttemptId: attempt.id,
          UserId: userId,
          ScheduledStartDate: start.toISOString(),
          ScheduledEndDate: end.toISOString(),
        });
      }
    }
  }

  return payload;
}

export function findDayOffsetForStagedAttempt(
  calendarSet: ICalendarSet | null | undefined,
  anchorDate: Moment | null | undefined,
  userId: string,
  calendarEvents: IAttemptListItemResponseForWorkOrder[],
  existingStagedForUser: StagedWorkOrderAssignment[],
  attempt: IAttemptListItemResponseForWorkOrder,
  maxDayOffsets: number = NHC_ASSIGNMENT_BOARD_DAY_COUNT,
  templateWindowMinutesById: Record<string, number> = EMPTY_TEMPLATE_REMAINING,
  templateWindowsById: Record<
    string,
    TimeWindowRange[]
  > = EMPTY_TEMPLATE_WINDOWS
): number {
  if (!anchorDate || maxDayOffsets < 1) {
    return 0;
  }

  const newMinutes = getExpectedDurationMinutes(attempt);
  const start = anchorDate.clone().startOf("day");
  const lastOffset = maxDayOffsets - 1;
  const hasWindows = hasTemplateWindowForAttempt(
    attempt,
    templateWindowMinutesById
  );

  for (let offset = 0; offset < maxDayOffsets; offset++) {
    const day = start.clone().add(offset, "days");
    if (getCalendarDayWorkingMinutes(calendarSet, day) == null) {
      return 0;
    }
    const stagedOnDay = existingStagedForUser.filter(
      (s) => s.dayOffset === offset
    );

    if (hasWindows) {
      const fits = canStageInTemplateWindow({
        attempt,
        userId,
        assignmentDate: day,
        calendarEvents,
        existingStagedForUser,
        templateWindowMinutesById,
        templateWindowsById,
      });
      if (fits) {
        return offset;
      }
    } else {
      const cursor = getScheduleCursorAfterEventsOnDay({
        day,
        userId,
        calendarEvents,
        stagedOnDay,
        calendarSet,
      });
      if (
        fitsOnDayAfterEvents({
          day,
          cursor,
          newMinutes,
          calendarSet,
        })
      ) {
        return offset;
      }
    }
  }

  return lastOffset;
}
